import { useEffect } from 'react';
import { validateServiceAccess } from '../helpers/AccessValidator';
import { LogDebug } from '../helpers/Logging';
import {
  ACCESS_TOKEN_COOKIE_NAME,
  COOKIE_DOMAIN,
  LOG_LEVEL_DEBUG,
  REFRESH_TOKEN_EXISTS_COOKIE_NAME,
} from '../constants/Constants';
import { isLocalhost } from '../helpers/Helpers';
import { IEmAuthCookies, ILoggerDetails, IEmServiceDetails } from '../types/types';
import Cookies from 'js-cookie';

interface IAuthVerificationParams {
  emService: IEmServiceDetails;
  emReAuthenticationServiceBaseUrl: string;
  logger?: ILoggerDetails;
}

/**
 * Custom React hook to check user authentication status and handle redirection.
 * REQUIRES consuming service to be on React 16.8+ (with hooks support).
 *
 * @param {IEmServiceDetails} emService - Details about the service using the middleware.
 * @param {string} emReAuthenticationServiceBaseUrl - URL for the re-authentication service.
 * @param {ILoggerDetails} logger - Logger instance for logging messages.
 */
export const useAuthVerification = (authVerificationParams: IAuthVerificationParams) => {
  const { emService, emReAuthenticationServiceBaseUrl, logger } = authVerificationParams;
  useEffect(() => {
    checkAuthVerificationClientSide(authVerificationParams);
  }, [emService, emReAuthenticationServiceBaseUrl, logger]);
};

/**
 * Performs client-side authentication verification and handles redirection if needed.
 * This function checks for bot detection, validates service access, clears expired cookies,
 * and redirects users to re-authentication if required.
 *
 * @param {IEmServiceDetails} emService - Details about the service using the middleware
 * @param {string} emReAuthenticationServiceBaseUrl - URL for the re-authentication service
 * @param {ILoggerDetails} logger - Optional logger instance for debugging messages
 * @returns {Promise<void>} Promise that resolves when authentication check is complete
 */
export const checkAuthVerificationClientSide = async ({
  emService,
  emReAuthenticationServiceBaseUrl,
  logger,
}: IAuthVerificationParams) => {
  const originalUrl = window.location.href;

  LogDebug(logger, `useAuthVerification() called on ${originalUrl}`);

  // Can't auth the bots
  const isBotDetected = Cookies.get('em_isBot') === 'true';

  if (isBotDetected) {
    LogDebug(logger, `useAuthVerification() bot detected, skipping auth check`);
    return;
  }

  const emAuthCookies: IEmAuthCookies = {
    em_at: Cookies.get(ACCESS_TOKEN_COOKIE_NAME),
    em_rte: Cookies.get(REFRESH_TOKEN_EXISTS_COOKIE_NAME),
  };

  LogDebug(logger, `useAuthVerification() em_at: ${emAuthCookies.em_at}, em_rte: ${emAuthCookies.em_rte}`);

  try {
    const { redirectUrl, emCookiesToTrash } = await validateServiceAccess({
      originalUrl: originalUrl,
      emService,
      emAuthCookies,
      emReAuthenticationServiceBaseUrl,
      isClientSide: true, // true because this is a client-side hook
      logger,
    });

    LogDebug(logger, `useAuthVerification() emCookiesToTrash: ${emCookiesToTrash}`);

    // Clear cookies if needed
    if (emCookiesToTrash?.length > 0) {
      const cookieDomain = isLocalhost(originalUrl) ? `localhost` : COOKIE_DOMAIN;
      emCookiesToTrash.forEach((cookie) => {
        //
        LogDebug(logger, `Deleting cookie: ${cookie}`);
        //
        document.cookie = `${cookie}=; domain=${cookieDomain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      });
    }

    LogDebug(logger, `useAuthVerification() redirectUrl: ${redirectUrl}`);

    // Redirect if needed
    if (redirectUrl) {
      // for debugging purposes, delay the redirect so DEVELOPER can see the console logs
      if (logger?.logLevel === LOG_LEVEL_DEBUG) {
        const debugRedirectDelaySeconds = 3;
        LogDebug(logger, `useAuthVerification() redirecting in ${debugRedirectDelaySeconds} seconds...`);
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, debugRedirectDelaySeconds * 1000); // 3 second delay for debugging
      } else {
        window.location.href = redirectUrl;
      }
    }
  } catch (error) {
    LogDebug(logger, `useAuthVerification() error: ${error}`);
  }
};
